import * as React from "react";

import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grid from "@mui/material/Grid";
import Grow from "@mui/material/Grow";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { styled, Theme } from "@mui/material/styles";

import { AmplitudeEvents } from "../../constants/amplitude";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { NextLink } from "../NextLink/NextLink";

import { ButtonProps, DashboardButton } from "@/dashboard-design-system/Button";
import { DashboardDivider } from "@/dashboard-design-system/Divider/DashboardDivider";
import { DashboardTooltip, DashboardTooltipProps } from "@/dashboard-design-system/Tooltip";
import { ChevronDown } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

const PREFIX = "DropdownButton";

const classes = {
  buttonGroup: `${PREFIX}-buttonGroup`,
  expandDownIcon: `${PREFIX}-expandDownIcon`,
  alignText: `${PREFIX}-alignText`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = React.memo(
  styled("div")(({ theme }: { theme: Theme }) => ({
    [`&& .${classes.buttonGroup}`]: {
      // Root styles aren't overridden normally
      boxShadow: "none !important",
    },

    [`&& .${classes.expandDownIcon}`]: {
      marginLeft: theme.spacing(1),
    },

    [`&& .${classes.alignText}`]: {
      justifyContent: "flex-start",
      paddingLeft: 0,
    },
  }))
);

const popperStyle = { zIndex: 10000 };

type PopperPlacement =
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "left"
  | "left-start"
  | "left-end";

export type DropdownButtonProps = {
  size?: ButtonProps["size"];
  variant?: ButtonProps["variant"];
  vibe?: ButtonProps["vibe"];
  buttonText?: string;
  onlyIcon?: IconElement;
  startIcon?: IconElement;
  endIcon?: IconElement;
  menuItemsProps: Array<
    Partial<Omit<MenuItemProps, "button" | "onClick">> & {
      key: string;
      children: React.ReactNode;
      href?: string;
      onClick?: (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
      tooltipProps?: Pick<DashboardTooltipProps, "title" | "placement">;
      "data-test"?: string;
      target?: string;
      isLoading?: boolean;
      disabled?: boolean;
    }
  >;
  isMobileLayout?: boolean;
  amplitudeEventOnClick?: AmplitudeEvents;
  onClick?: (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
  buttonRef?: React.RefObject<HTMLButtonElement | null>;
  "data-test"?: string;
  disabled?: boolean;
  placement?: PopperPlacement;
};

export interface DropdownButtonRef {
  setOpen: (input: boolean) => void;
}

export const DropdownButton = React.forwardRef<DropdownButtonRef, DropdownButtonProps>(
  (
    {
      buttonText,
      menuItemsProps,
      vibe = "brand",
      variant = "filled",
      size,
      startIcon,
      onlyIcon,
      endIcon,
      isMobileLayout,
      amplitudeEventOnClick,
      onClick,
      buttonRef,
      "data-test": dataTest,
      disabled,
      placement = "bottom",
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const { logAmplitudeEvent } = useAmplitude();

    const handleToggle = React.useCallback(
      (event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => {
        if (onClick) {
          onClick(event);
        }
        setOpen((prevOpen) => !prevOpen);
        if (amplitudeEventOnClick) {
          logAmplitudeEvent(amplitudeEventOnClick);
        }
      },
      [amplitudeEventOnClick, logAmplitudeEvent, onClick]
    );

    const onClickOnAMenuButton = React.useCallback(() => {
      setOpen(false);
    }, []);

    const handleClose = React.useCallback((event: MouseEvent | TouchEvent) => {
      if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
        return;
      }
      setOpen(false);
    }, []);

    React.useImperativeHandle(
      ref,
      () => ({
        setOpen: (input: boolean) => {
          setOpen(input);
        },
      }),
      []
    );

    if (isMobileLayout) {
      return (
        <Root>
          {menuItemsProps.map((prop) => {
            const { children, key, onClick, "data-test": dataTest, href, ..._restProp } = prop;
            return (
              <Grid item key={key}>
                <DashboardDivider />
                <DashboardButton
                  onClick={onClick}
                  variant="text"
                  vibe="neutral"
                  fullWidth
                  className={classes.alignText}
                  data-test={dataTest}
                  href={href}
                  target={prop.target}
                >
                  {children}
                </DashboardButton>
              </Grid>
            );
          })}
        </Root>
      );
    }

    return (
      <Root>
        <ButtonGroup
          variant="contained"
          ref={anchorRef}
          aria-label="split button"
          className={classes.buttonGroup}
          fullWidth
        >
          <DashboardButton
            vibe={vibe}
            variant={variant}
            size={size}
            aria-controls={open ? "split-button-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="select merge strategy"
            aria-haspopup="menu"
            onClick={handleToggle}
            startIcon={startIcon}
            endIcon={endIcon ?? (onlyIcon ? undefined : <ChevronDown />)}
            data-test={dataTest}
            ref={buttonRef}
            disabled={disabled}
            fullWidth
            iconButton={!!onlyIcon}
          >
            {buttonText || onlyIcon}
          </DashboardButton>
        </ButtonGroup>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          onClick={onClickOnAMenuButton}
          style={popperStyle}
          placement={placement}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement.includes("bottom")
                  ? "center top"
                  : placement.includes("top")
                    ? "center bottom"
                    : placement.includes("right")
                      ? "left center"
                      : "right center",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem>
                    {menuItemsProps.map((prop) => {
                      const {
                        children,
                        key,
                        href,
                        "data-test": dataTest,
                        tooltipProps,
                        target,
                        isLoading,
                        disabled,
                        ...restProp
                      } = prop;
                      const currentDisabled = disabled || isLoading;
                      if (href) {
                        return (
                          <NextLink href={href} target={target}>
                            <MenuItem key={key} disabled={currentDisabled} {...restProp} data-test={dataTest}>
                              {children}
                            </MenuItem>
                          </NextLink>
                        );
                      }
                      if (tooltipProps) {
                        return (
                          <DashboardTooltip key={key} {...tooltipProps}>
                            <span>
                              <MenuItem disabled={currentDisabled} {...restProp}>
                                {children}
                              </MenuItem>
                            </span>
                          </DashboardTooltip>
                        );
                      }
                      return (
                        <MenuItem key={key} disabled={currentDisabled} {...restProp} data-test={dataTest}>
                          {children}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Root>
    );
  }
);

DropdownButton.displayName = "DropdownButton";
